import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Check, 
  Crown,
  AlertCircle,
  Mail,
  Phone,
  Calendar,
  Gift,
  Clock
} from 'lucide-react';

const formatPrice = (p) => (typeof p === 'number' ? p.toFixed(2).replace('.', ',') : p);

const ContactBlock = () => (
  <div className="bg-white rounded-xl p-4 space-y-3">
    <div className="flex items-center gap-3">
      <Mail className="h-5 w-5 text-orange-500" />
      <a href="mailto:contact@dealrun.re" className="text-gray-700 font-medium hover:text-orange-500">
        contact@dealrun.re
      </a>
    </div>
    <div className="flex items-center gap-3">
      <Phone className="h-5 w-5 text-orange-500" />
      <a href="tel:0693463628" className="text-gray-700 font-medium hover:text-orange-500">
        06 93 46 36 28
      </a>
    </div>
  </div>
);

const Billing = () => {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const { subscriptionStatus } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/subscriptions/current');
      setCurrentPlan(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const status = currentPlan?.subscription_status || subscriptionStatus;
  const isPending = status === 'pending';
  const isTrial = status === 'trial';
  const isExpired = status === 'expired';
  const trialDays = currentPlan?.trial_days_remaining;
  const trialEndingSoon = isTrial && trialDays != null && trialDays <= 7;

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <CreditCard className="h-8 w-8 text-orange-500" />
          Abonnement
        </h1>
        <p className="text-gray-500 mt-1">
          Votre forfait actuel
        </p>
      </div>

      {/* Trial in progress */}
      {isTrial && (
        <Card className={trialEndingSoon ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'} data-testid="trial-alert">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${trialEndingSoon ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                {trialEndingSoon ? <Clock className="h-6 w-6 text-amber-600" /> : <Gift className="h-6 w-6 text-emerald-600" />}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h2 className="font-heading font-bold text-lg text-gray-900">
                    Essai gratuit en cours
                  </h2>
                  <Badge className={`border-0 ${trialEndingSoon ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'}`} data-testid="trial-days-badge">
                    {trialDays} jour{trialDays > 1 ? 's' : ''} restant{trialDays > 1 ? 's' : ''}
                  </Badge>
                </div>
                <p className="text-gray-600 mb-1">
                  Vous profitez de toutes les fonctionnalités gratuitement
                  {currentPlan?.trial_end && (
                    <> jusqu'au <span className="font-semibold text-gray-900">{new Date(currentPlan.trial_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span></>
                  )}.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Ensuite : <span className="font-semibold text-gray-700">19,90€ HT/mois</span> — Sans engagement, résiliable à tout moment.
                </p>
                <p className="text-sm font-medium text-gray-700 mb-3">Pour activer votre abonnement, contactez-nous :</p>
                <ContactBlock />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expired */}
      {isExpired && (
        <Card className="border-red-200 bg-red-50" data-testid="expired-alert">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-bold text-lg text-gray-900 mb-2">
                  Votre essai gratuit est terminé
                </h2>
                <p className="text-gray-600 mb-1">
                  Votre accès est suspendu, mais toutes vos données sont conservées.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Réactivez votre compte pour <span className="font-semibold text-gray-700">19,90€ HT/mois</span> — Sans engagement.
                </p>
                <ContactBlock />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending - Contact Admin (legacy accounts) */}
      {isPending && (
        <Card className="border-orange-200 bg-orange-50" data-testid="pending-alert">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-orange-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-bold text-lg text-gray-900 mb-2">
                  Votre forfait n'est pas encore actif
                </h2>
                <p className="text-gray-600 mb-4">
                  Merci de vous rapprocher de l'administrateur pour activer votre forfait.
                </p>
                <ContactBlock />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Display */}
      {!isPending && currentPlan?.details && (
        <Card className="border-0 shadow-soft" data-testid="active-plan">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                <Crown className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">{isTrial ? 'Forfait après votre essai' : 'Forfait actuel'}</p>
                <p className="font-heading font-bold text-2xl text-gray-900">
                  {currentPlan?.details?.name || 'Formule unique'}
                </p>
              </div>
              {isTrial ? (
                <Badge className="bg-blue-100 text-blue-700 border-0 px-3 py-1 text-sm">Essai</Badge>
              ) : isExpired ? (
                <Badge className="bg-red-100 text-red-700 border-0 px-3 py-1 text-sm">Expiré</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 border-0 px-3 py-1 text-sm">Actif</Badge>
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-5 mb-6">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-4xl font-bold text-gray-900" data-testid="plan-price">
                  {formatPrice(currentPlan?.details?.price) || '—'}€
                </span>
                <span className="text-gray-500">/mois HT</span>
              </div>
              <p className="text-sm text-gray-400">Sans engagement — Résiliable à tout moment</p>
              {!isTrial && currentPlan?.subscription_end && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Fin du forfait : <span className="font-semibold text-gray-900">{new Date(currentPlan.subscription_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </span>
                </div>
              )}
            </div>

            {currentPlan?.details?.features && (
              <ul className="space-y-3">
                {currentPlan.details.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                    <Check className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Contact for changes */}
      <Card className="border-0 shadow-soft">
        <CardContent className="p-5 text-center">
          <p className="text-gray-500 text-sm">
            Pour toute modification de votre forfait, contactez-nous à{' '}
            <a href="mailto:contact@dealrun.re" className="text-orange-500 font-medium hover:underline">contact@dealrun.re</a>
            {' '}ou au{' '}
            <a href="tel:0693463628" className="text-orange-500 font-medium hover:underline">06 93 46 36 28</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;
